import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `€ ${new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(n)}`;

export default function Admin() {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [properties, setProperties] = useState([]);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("ascari_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    axios
      .get(`${API}/auth/me`, auth)
      .then((res) => {
        setUser(res.data);
        axios.get(`${API}/admin/leads`, auth).then((r) => setLeads(r.data)).catch(() => {});
        axios.get(`${API}/admin/estimates`, auth).then((r) => setEstimates(r.data)).catch(() => {});
        axios.get(`${API}/properties`).then((r) => setProperties(r.data)).catch(() => {});
      })
      .catch(() => {
        localStorage.removeItem("ascari_admin_token");
        navigate("/admin/login");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("ascari_admin_token");
    navigate("/admin/login");
  };

  const updateProperty = async (id, fields) => {
    try {
      const { data } = await axios.put(`${API}/admin/properties/${id}`, fields, auth);
      setProperties(properties.map((p) => (p.id === id ? data : p)));
      toast.success("Annuncio aggiornato.");
    } catch (err) {
      toast.error("Aggiornamento non riuscito.");
    }
  };

  const tabBtn = (key, label) => (
    <button
      key={key}
      onClick={() => setTab(key)}
      data-testid={`admin-tab-${key}`}
      className={`px-4 py-2 font-mono-lbl text-xs uppercase tracking-wider rounded transition-colors ${
        tab === key ? "bg-[var(--ink)] text-[var(--gold-light)]" : "bg-white border border-[var(--ink)]/15 text-[var(--ink)]/70 hover:border-[var(--gold)]"
      }`}
    >
      {label}
    </button>
  );

  if (!user) {
    return (
      <section className="bg-white py-32 px-6">
        <p className="max-w-6xl mx-auto font-mono-lbl text-xs uppercase text-[var(--ink)]/50">Verifica accesso...</p>
      </section>
    );
  }

  return (
    <section className="bg-[var(--paper)] min-h-[70vh] py-16 px-6" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-deep)] block mb-1">
              Area Riservata
            </span>
            <h1 className="font-display font-bold text-3xl">Gestione Agenzia</h1>
            <p className="text-xs text-[var(--ink)]/60 mt-1 font-mono-lbl">{user.email}</p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="px-4 py-2 bg-white border border-[var(--ink)]/15 font-mono-lbl text-xs uppercase tracking-wider rounded hover:border-[var(--gold)] transition-colors"
          >
            Esci
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          {tabBtn("leads", `Richieste (${leads.length})`)}
          {tabBtn("estimates", `Valutazioni (${estimates.length})`)}
          {tabBtn("properties", `Annunci (${properties.length})`)}
        </div>

        {tab === "leads" && (
          <div className="space-y-4" data-testid="admin-leads-list">
            {leads.length === 0 && <p className="text-sm text-[var(--ink)]/60">Nessuna richiesta ancora.</p>}
            {leads.map((l) => (
              <div key={l.id} className="bg-white border border-[var(--ink)]/10 rounded-lg p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <span className="font-display font-bold">{l.nominativo}</span>
                  <span className="font-mono-lbl text-[10px] uppercase text-[var(--gold-deep)] font-semibold">{l.richiesta}</span>
                </div>
                <p className="text-sm text-[var(--ink)]/75">
                  Tel: <a href={`tel:${l.telefono}`} className="text-[var(--gold-deep)] font-semibold">{l.telefono || "—"}</a>
                  {l.email && (
                    <>
                      {" · Email: "}
                      <a href={`mailto:${l.email}`} className="text-[var(--gold-deep)] font-semibold">{l.email}</a>
                    </>
                  )}
                </p>
                {l.note && <p className="text-xs text-[var(--ink)]/60 mt-1">{l.note}</p>}
                <p className="font-mono-lbl text-[10px] text-[var(--ink)]/40 mt-2">
                  {new Date(l.created_at).toLocaleString("it-IT")}
                </p>
              </div>
            ))}
          </div>
        )}

        {tab === "estimates" && (
          <div className="space-y-4" data-testid="admin-estimates-list">
            {estimates.length === 0 && <p className="text-sm text-[var(--ink)]/60">Nessuna valutazione ancora.</p>}
            {estimates.map((e) => (
              <div key={e.id} className="bg-white border border-[var(--ink)]/10 rounded-lg p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <span className="font-display font-bold">
                    {e.destinazione} · {e.mq} mq · {e.zona}
                  </span>
                  <span className="font-mono-lbl text-xs font-bold text-[var(--gold-deep)]">
                    {eur(e.stima_min)} – {eur(e.stima_max)}
                  </span>
                </div>
                <p className="text-xs text-[var(--ink)]/70">
                  {e.piano} · {e.stato} · {e.riscaldamento} · Spese € {e.spese_condominiali}/mese
                </p>
                {e.email && (
                  <p className="text-xs mt-1">
                    Email: <a href={`mailto:${e.email}`} className="text-[var(--gold-deep)] font-semibold">{e.email}</a>
                  </p>
                )}
                {e.descrizione && <p className="text-xs text-[var(--ink)]/60 mt-1">{e.descrizione}</p>}
                <p className="font-mono-lbl text-[10px] text-[var(--ink)]/40 mt-2">
                  {new Date(e.created_at).toLocaleString("it-IT")}
                </p>
              </div>
            ))}
          </div>
        )}

        {tab === "properties" && (
          <div className="space-y-4" data-testid="admin-properties-list">
            {properties.map((p) => (
              <AdminPropertyRow key={p.id} property={p} onSave={updateProperty} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function AdminPropertyRow({ property, onSave }) {
  const [prezzo, setPrezzo] = useState(property.prezzo_label);
  const [descrizione, setDescrizione] = useState(property.descrizione);
  const [editing, setEditing] = useState(false);
  const tid = property.titolo.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <div className="bg-white border border-[var(--ink)]/10 rounded-lg p-5" data-testid={`admin-property-${tid}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-mono-lbl text-[10px] uppercase text-[var(--gold-deep)] font-semibold">
            {property.tipo === "affitto" ? "Affitto" : "Vendita"}
          </span>
          <h3 className="font-display font-bold">{property.titolo}</h3>
        </div>
        <button
          onClick={() => setEditing(!editing)}
          data-testid={`admin-edit-${tid}`}
          className="px-3 py-1.5 bg-[var(--paper)] border border-[var(--ink)]/15 font-mono-lbl text-[10px] uppercase tracking-wider rounded hover:border-[var(--gold)] transition-colors"
        >
          {editing ? "Chiudi" : "Modifica"}
        </button>
      </div>
      {editing && (
        <div className="mt-4 space-y-3 pt-4 border-t border-[var(--ink)]/10">
          <div>
            <label className="block font-mono-lbl text-[10px] uppercase tracking-wider mb-1">Prezzo</label>
            <input
              value={prezzo}
              onChange={(e) => setPrezzo(e.target.value)}
              data-testid={`admin-price-${tid}`}
              className="w-full sm:w-64 bg-[var(--paper)] border border-[var(--ink)]/20 rounded p-2 text-sm focus:border-[var(--gold)] outline-none"
            />
          </div>
          <div>
            <label className="block font-mono-lbl text-[10px] uppercase tracking-wider mb-1">Descrizione</label>
            <textarea
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              rows="3"
              data-testid={`admin-desc-${tid}`}
              className="w-full bg-[var(--paper)] border border-[var(--ink)]/20 rounded p-2 text-sm focus:border-[var(--gold)] outline-none"
            ></textarea>
          </div>
          <button
            onClick={() => onSave(property.id, { prezzo_label: prezzo, descrizione })}
            data-testid={`admin-save-${tid}`}
            className="px-5 py-2.5 bg-[var(--ink)] text-[var(--gold-light)] font-mono-lbl text-[10px] uppercase tracking-widest font-bold rounded hover:bg-[var(--gold)] hover:text-[var(--ink)] transition-colors"
          >
            Salva Modifiche
          </button>
        </div>
      )}
    </div>
  );
}
